import { Component, OnInit, HostListener } from '@angular/core';
import { Router, NavigationEnd, ActivatedRoute } from '@angular/router';
import { trigger, state, style, transition, animate } from '@angular/animations';
import * as moment from 'moment-timezone';
import * as _ from 'lodash';
import { SharedService } from 'app/shared/services/shared.service';
import { ReportsService } from 'app/shared/services/reports.service';
import { UserService } from 'app/shared/services/user.service';
import { DictionaryService } from 'app/shared/services/dictionary.service';
import { GoogleAnalyticsService } from 'app/shared/services/google-analytics.service';

@Component({
  selector: 'side-menu',
  templateUrl: './side-menu.component.html',
  styleUrls: ['./side-menu.component.scss'],
  animations: [
    trigger('showAndHideAnimation', [
      state(
        'hide',
        style({
          visibility: 'hidden',
          opacity: 0,
        })
      ),
      state(
        'show',
        style({
          visibility: 'visible',
          opacity: 1,
        })
      ),
      transition('hide <=> show', animate('200ms')),
    ]),
  ],
})
export class SideMenuComponent implements OnInit {
  public allMenuItems: Array<any> = [];
  public menuItems: Array<any> = [];
  public myAccountMenuItems: Array<any> = [];
  public myAccountMarginTop: number;
  public estTime: string;
  public filteredNetworks: Array<any>;
  public networkListToggleStatus: boolean;
  public networkListMarginTop: number;
  public networkFilterInputValue: string;
  public networkFilterInputValueOld: string;
  public hoveredNetwork: any;

  constructor(
    public router: Router,
    public route: ActivatedRoute,
    public sharedService: SharedService,
    public reportsService: ReportsService,
    public userService: UserService,
    public dictionaryService: DictionaryService,
    public ga: GoogleAnalyticsService
  ) {
    this.allMenuItems = [
      {
        id: 'recent',
        label: 'RECENT',
        icon: 'clock-o',
        userTypes: ['adv', 'admin'],
        subItems: [],
      },
      {
        id: 'supply',
        label: 'SUPPLY',
        icon: 'play-circle',
        userTypes: ['adv', 'admin'],
        subItems: [
          {
            label: 'MANAGE_SUPPLY',
            icon: 'pencil-square-o',
            link: '/supply/manage',
            dictionary: true,
            uppercase: true,
            innerLinks: ['/supply/manage'],
          },
        ],
      },
      {
        id: 'demand',
        label: 'DEMAND',
        icon: 'caret-square-o-down',
        userTypes: ['adv', 'admin'],
        subItems: [
          {
            label: 'MANAGE_DEMAND',
            icon: 'pencil-square-o',
            link: '/demand/manage',
            dictionary: true,
            uppercase: true,
            innerLinks: ['/demand/manage'],
          },
        ],
      },
      {
        id: 'myReports',
        label: 'MY_REPORTS',
        icon: 'area-chart',
        userTypes: ['adv', 'admin', 'cedatopub'],
        subItems: [
          {
            label: 'CREATE',
            icon: 'plus',
            link: '/reports/new',
            dictionary: true,
            uppercase: true,
            mandatory: true,
            data: { new: true },
          },
        ],
      },
      {
        id: 'more',
        label: 'MORE',
        image: './assets/img/icons/side_menu_more.svg',
        userTypes: ['adv', 'admin'],
        subItems: [
          // {
          //   label: 'QUALITY_AND_VIEWABILITY',
          //   link: '/quality',
          //   icon: 'gavel',
          //   dictionary: true,
          //   uppercase: true,
          //   innerLinks: ['/quality/supply', '/quality/demand'],
          // },
          {
            label: 'MANAGE_RTB_PARTNERS',
            link: '/rtb-partners/manage',
            icon: 'handshake-o',
            dictionary: true,
            uppercase: true,
            hideFromPublishersNetwork: true,
            innerLinks: ['/rtb-partners/manage'],
            featureFlag: 'rtb_demand',
          },
          {
            label: 'PUBLISHERS',
            link: '/publishers/manage',
            icon: 'industry',
            dictionary: true,
            uppercase: true,
            hideFromPublishersNetwork: true,
            innerLinks: ['/publishers/manage'],
          },
          {
            label: 'MANAGE_LISTS',
            link: '/list/manage',
            image: './assets/img/icons/side_menu_list.svg',
            dictionary: true,
            uppercase: true,
            hideFromPublishersNetwork: true,
            innerLinks: ['/list/manage'],
          },
          {
            label: 'MANAGE_CDN',
            link: '/cdn/manage',
            icon: 'file-text',
            dictionary: true,
            uppercase: true,
            featureFlag: 'display_cdn',
          },
          {
            label: 'CDN_USAGE',
            link: '/cdn/usage',
            icon: 'bar-chart',
            dictionary: true,
            uppercase: true,
            featureFlag: 'display_cdn',
          },
        ],
      },
      {
        id: 'admin',
        label: 'ADMIN',
        icon: 'cog',
        userTypes: ['admin'],
        subItems: [
          { label: 'BROADCAST', link: '/admin/broadcast', icon: 'bullhorn', dictionary: true, uppercase: true },
          { label: 'NETWORKS', link: '/admin/networks', icon: 'sitemap', dictionary: true, uppercase: true },
          { label: 'USERS', link: '/admin/users', icon: 'users', dictionary: true, uppercase: true },
          {
            label: 'REPORTS',
            icon: 'bar-chart',
            uppercase: true,
            subItems: [
              { label: 'ALL_SUPPLY', link: '/admin/all-supply', icon: 'line-chart', dictionary: true, uppercase: true },
              { label: 'ALL_DEMAND', link: '/admin/all-demand', icon: 'line-chart', dictionary: true, uppercase: true },
              { label: 'EXCO_REPORT', link: '/admin/exco-report', icon: 'line-chart', dictionary: true, uppercase: true },
              { label: 'RELEASE_DASHBOARD', link: '/admin/release-dashboard', icon: 'line-chart', dictionary: true, uppercase: true },
              { label: 'NETWORKS_REPORT', link: '/admin/networks-report', icon: 'line-chart', dictionary: true, uppercase: true },
              { label: 'CEDATO_LABS', link: '/admin/cedato-labs', icon: 'flask', dictionary: true, uppercase: true },
              {
                label: 'CEDATO_LABS_V2',
                link: '/admin/cedato-labs-v2',
                icon: 'flask',
                dictionary: true,
                uppercase: true,
              },
              { label: 'BILLING', link: '/admin/billing', icon: 'money', dictionary: true, uppercase: true },
            ],
          },
          { label: 'DEBUG_TOOL', link: '/admin/debug-tool', icon: 'list-alt', dictionary: true, uppercase: true },
          { label: 'DEVELOPER_TOOLS', link: '/admin/dev-tools', icon: 'code', dictionary: true, uppercase: true },
          {
            label: 'AUTOMATIC_REPORTS',
            link: '/admin/automatic-reports',
            icon: 'bar-chart',
            dictionary: true,
            uppercase: true,
          },
        ],
      },
    ];
    this.myAccountMenuItems = [
      {
        label: 'HELP_CENTER',
        link: 'https://support.cedato.com/support/home',
        userTypes: ['adv', 'admin'],
        external: true,
      },
      { label: 'USER_SETTINGS', link: '/settings/user', userTypes: ['adv', 'admin'] },
      { label: 'NETWORK_SETTINGS', link: '/settings/network', userTypes: ['adv', 'admin'] },
      { label: 'SIGN_OUT', callback: this.signout },
    ];
    this.filteredNetworks = [];
    this.networkListToggleStatus = false;
    this.networkFilterInputValue = '';
    this.networkFilterInputValueOld = '';
    this.hoveredNetwork = null;
  }

  ngOnInit() {
    let currentURL: string;
    this.initMenuItems();
    this.closeAllSideMenuSubItems();
    this.openActiveMenuItem();
    this.updateFixedElementsLocations();
    this.updateClock();
    this.sharedService.getMessagesBus().subscribe((response: any) => {
      if (response.type === 'NETWORK_DATA_UPDATED') {
        this.initMenuItems();
      } else if (response.type === 'RECENT_REPORTS_UPDATED') {
        this.updateRecentReports(response.data);
      } else if (response.type === 'CUSTOM_REPORTS_UPDATED') {
        this.updateCustomReports(response.data);
      } else if (response.type === 'SIDE_MENU_STATE_CHANGE') {
        this.closeAllSideMenuSubItems(true);
      }
    });

    this.router.events.subscribe((event: any) => {
      if (event instanceof NavigationEnd) {
        currentURL = event.url.split('?')[0];
        currentURL = decodeURI(currentURL.replace(/^\//g, ''));
        this.updateActiveItem(currentURL, true);
      }
    });
    if (this.userService.isAdmin()) {
      this.filteredNetworks = _.cloneDeep(this.sharedService.activeNetworks);
    }
  }

  ngOnChanges($event: any) {
    this.closeAllSideMenuSubItems();
  }

  initMenuItems() {
    let defaultSupplyReports: Array<any>;
    let defaultDemandReports: Array<any>;
    let defaultCedatoPublisherReports: Array<any>;
    let defaultContentReports: Array<any>;
    let userTypePass: boolean;
    let publisherPass: boolean;
    let featureFlagPass: boolean;
    let currentMenuItem: any;
    let currentMenuSubItem: any;
    // Copy all of the items
    this.menuItems = _.cloneDeep(this.allMenuItems);
    this.reportsService.buildDefaultReports();
    // Add the default reports
    if (!this.userService.isPublisher() && !this.userService.isCedatoPublisher()) {
      defaultSupplyReports = this.userService.isAdmin()
        ? _.filter(this.reportsService.defaultReports, { primaryKey: 'supply' })
        : _.filter(this.reportsService.defaultReports, { primaryKey: 'supply', admin: false });

      defaultDemandReports = this.userService.isAdmin()
        ? _.filter(this.reportsService.defaultReports, { primaryKey: 'demand' })
        : _.filter(this.reportsService.defaultReports, { primaryKey: 'demand', admin: false });

      if (this.userService.getFeatureState('content_reports')) {
        defaultContentReports = _.filter(this.reportsService.defaultReports, { primaryKey: 'content' });
      }

      if (defaultContentReports && defaultContentReports.length) {
        currentMenuItem = _.findIndex(this.menuItems, { id: 'more' });
        for (let i = 0; i < defaultContentReports.length; i++) {
          this.menuItems[currentMenuItem].subItems.push({
            label: defaultContentReports[i].name,
            icon: 'bar-chart',
            link: defaultContentReports[i].link,
            dictionary: true,
            uppercase: true,
          });
        }
      }

      currentMenuItem = _.findIndex(this.menuItems, { id: 'supply' });
      for (let i = 0; i < defaultSupplyReports.length; i++) {
        this.menuItems[currentMenuItem].subItems.push({
          label: defaultSupplyReports[i].name,
          icon: 'bar-chart',
          link: defaultSupplyReports[i].link,
          dictionary: true,
          uppercase: true,
        });
      }

      currentMenuItem = _.findIndex(this.menuItems, { id: 'demand' });
      for (let i = 0; i < defaultDemandReports.length; i++) {
        this.menuItems[currentMenuItem].subItems.push({
          label: defaultDemandReports[i].name,
          icon: 'bar-chart',
          link: defaultDemandReports[i].link,
          dictionary: true,
          uppercase: true,
        });
      }

      //Manage Campaigns
      if (this.userService.getFeatureState('manage_campaigns')) {
        currentMenuItem = _.findIndex(this.menuItems, { id: 'more' });
        currentMenuSubItem = _.findIndex(this.menuItems[currentMenuItem], { label: 'PUBLISHERS' });
        this.menuItems[currentMenuItem].subItems.splice(currentMenuSubItem, 0, {
          label: 'MANAGE_CAMPAIGNS',
          link: '/campaigns/manage',
          icon: 'tasks',
          dictionary: true,
          uppercase: true,
          hideFromPublishersNetwork: true,
          innerLinks: ['/publishers/manage'],
        });
      }
    } else if (this.userService.isCedatoPublisher()) {
      defaultCedatoPublisherReports = _.filter(this.reportsService.defaultReports, { primaryKey: 'cedatopub' });
      currentMenuItem = _.findIndex(this.menuItems, { id: 'myReports' });
      for (let i = 0; i < defaultCedatoPublisherReports.length; i++) {
        this.menuItems[currentMenuItem].subItems.unshift({
          label: defaultCedatoPublisherReports[i].name,
          icon: 'bar-chart',
          link: defaultCedatoPublisherReports[i].link,
          dictionary: true,
          uppercase: true,
          mandatory: true,
        });
      }
    }

    // Remove the 'Admin' item, if the current user is not an admin
    for (let i: number = this.menuItems.length - 1; i > -1; i--) {
      userTypePass =
        this.menuItems[i].userTypes &&
        this.userService.userData.userType &&
        this.menuItems[i].userTypes.length &&
        this.menuItems[i].userTypes.includes(this.userService.userData.userType);
      publisherPass = !(this.menuItems[i].hideFromPublishersNetwork && this.userService.isPublisherNetwork());
      this.userService.isCedatoPublisher();

      if (!userTypePass || !publisherPass) {
        this.menuItems.splice(i, 1);
      } else {
        // Remove feature flagged items, if the user doesn't have this feature flag
        if (this.menuItems[i].subItems) {
          this.menuItems[i].subItems.forEach((subItem: any) => {
            featureFlagPass = !(subItem.featureFlag && !this.userService.getFeatureState(subItem.featureFlag));
            publisherPass = !(subItem.hideFromPublishersNetwork && this.userService.isPublisherNetwork());
            if (!featureFlagPass || !publisherPass) {
              this.menuItems[i].subItems = _.without(this.menuItems[i].subItems, subItem);
            }
          });
        }
      }
    }

    for (let i: number = this.myAccountMenuItems.length - 1; i > -1; i--) {
      userTypePass =
        this.myAccountMenuItems[i].userTypes &&
        this.userService.userData.userType &&
        this.myAccountMenuItems[i].userTypes.length &&
        this.myAccountMenuItems[i].userTypes.includes(this.userService.userData.userType);
      if (!userTypePass && this.myAccountMenuItems[i].userTypes) {
        this.myAccountMenuItems.splice(i, 1);
      }
    }

    // Remove user-role based items
    this.menuItems.forEach((menuItem: any) => {
      if (menuItem.subItems) {
        menuItem.subItems.forEach((subItem: any) => {
          if (subItem.userRole) {
            if (!this.userService.getUserRoleState(subItem.userRole)) {
              menuItem.subItems = _.without(menuItem.subItems, subItem);
            }
          }
        });
      }
    });

    // Custom reports ("My reports")
    this.updateCustomReports();
    this.setCurrentMenuItemState();
  }

  setCurrentMenuItemState() {
    setTimeout(() => {
      let currentURL: string = this.router.url.split('?')[0];
      currentURL = decodeURI(currentURL.replace(/^\//g, ''));
      if (this.sharedService.sideMenuState !== 'small') { this.updateActiveItem(currentURL); }
    }, 100);
  }

  updateActiveItem(currentURL: string = '', isNavigation: boolean = false) {
    for (let i = 0; i < this.menuItems.length; i++) {
      this.menuItems[i]['active'] = false;
      if (this.menuItems[i]['subItems'] && this.menuItems[i]['subItems'].length) {
        this.menuItems[i] = this.checkIsActiveSubItem(currentURL, this.menuItems[i], i);
      }
    }

    if (isNavigation && this.sharedService.sideMenuState === 'small') {
      this.closeAllSideMenuSubItems(true);
    }
  }

  checkIsActiveSubItem(currentURL: string = '', items: any = {}, i: number = -1) {
    for (let i = 0; i < items.subItems.length; i++) {
      items.subItems[i]['active'] =
        items.subItems[i]['link'] &&
        items.id !== 'recent' &&
        (decodeURI(items.subItems[i]['link'].replace(/^\//g, '')) === currentURL ||
          decodeURI(items.subItems[i]['link'].replace(/^\//g, '')) === currentURL.replace(/\/\d+$/, ''));

      if (items.subItems[i]['subItems'] && items.subItems[i]['subItems'].length) {
        items.subItems[i] = this.checkIsActiveSubItem(currentURL, items.subItems[i], i);
      }

      if (items.subItems[i]['active'] && i > -1 && items.id !== 'recent') { items['active'] = true; }
    }

    return items;
  }

  updateCustomReports(loadReportParams: any = {}) {
    let queryParams: any = {};
    const myReportIndex = _.findIndex(this.menuItems, { id: 'myReports' });
    if (myReportIndex < 0) { return; }

    this.menuItems[myReportIndex].subItems = _.filter(this.menuItems[myReportIndex].subItems, (one) => one.mandatory);
    for (let i = 0; i < this.reportsService.customReports.length; i++) {
      queryParams = {
        id: this.reportsService.customReports[i].reportId,
        d: this.reportsService.customReports[i].dimensions,
      };
      if (!this.sharedService.isEmpty(this.reportsService.customReports[i].sorting)) {
        queryParams.s = this.reportsService.customReports[i].sorting;
      }
      if (!this.sharedService.isEmpty(this.reportsService.customReports[i].filters)) {
        queryParams.f = this.reportsService.customReports[i].filters;
      }
      this.menuItems[myReportIndex].subItems.push({
        label: this.reportsService.customReports[i].name,
        link: this.reportsService.customReports[i].link,
        queryParams: queryParams,
        icon: 'bar-chart',
        dictionary: false,
        uppercase: false,
      });
    }

    this.updateRecentReports(loadReportParams);
  }

  updateRecentReports(loadReportParams: any) {
    let currReport: any;
    let currReportName: string;
    let currReportId: number;
    let queryParams: any = {};
    let currCustomReport;
    const customReportsIndex = _.findIndex(this.menuItems, { id: 'recent' });

    if (customReportsIndex < 0) { return false; }

    this.menuItems[customReportsIndex].subItems = [];
    for (let i = 0; i < this.reportsService.recentReports.length; i++) {
      currReport = this.reportsService.recentReports[i];
      currReportName = '';
      currReportId = parseInt(currReport.id);
      if (currReport.id) {
        currCustomReport = _.find(this.reportsService.customReports, function (o) {
          return parseInt(o.reportId) === currReportId;
        });
        if (currCustomReport) {
          currReportName = currCustomReport.reportName;
        }
      } else {
        currReportName = currReport.name;
      }
      if (!this.sharedService.isEmpty(currReportName)) {
        queryParams = currReport.queryParams ? _.cloneDeep(currReport.queryParams) : {};
        this.menuItems[customReportsIndex].subItems.push({
          label: currReportName,
          link: currReport.link,
          queryParams: queryParams,
          icon: 'bar-chart',
          dictionary: false,
          uppercase: false,
        });
      }
    }

    if (loadReportParams && !_.isEmpty(loadReportParams)) {
      if (!loadReportParams.id) {
        this.router.navigate(['/reports/new'], { queryParams: loadReportParams });
      } else {
        const find = _.find(this.reportsService.customReports, { id: loadReportParams.id });
        if (find) {
          const params: any = {
            d: find.dimensions ? find.dimensions : null,
            f: find.filters ? find.filters : null,
            s: find.sorting ? find.sorting : null,
            id: find.id ? find.id : null,
            ds: find.ds ? find.ds : null,
            nid: find.nid ? find.nid : null,
          };
          this.router.navigate([find.link], { queryParams: params });
        }
      }
    }
  }

  toggleSideMenuSubItems(item: any) {
    for (let i = 0; i < this.menuItems.length; i++) {
      if (this.menuItems[i].subItems) {
        this.menuItems[i].opened = item.label === this.menuItems[i].label ? !this.menuItems[i].opened : false;
      }
    }
    switch (item.id) {
      case 'recent':
        this.ga.sendEvent(this.ga.MENU, 'Recent title', this.ga.RECENT);
        break;
      case 'myReports':
        this.ga.sendEvent(this.ga.MENU, 'My Reports title', this.ga.MY_REPORTS);
        break;
      case 'supply':
        this.ga.sendEvent(this.ga.MENU, 'Supply title', this.ga.SUPPLY);
        break;
      case 'demand':
        this.ga.sendEvent(this.ga.MENU, 'Demand title', this.ga.DEMAND);
        break;
    }
  }

  toggleSideMenuSubItemsSecondLevel(item: any) {
    item.active = !item.active;
    item.opened = !item.opened;
  }

  closeAllSideMenuSubItems(isForce: boolean = false, menuItems: Array<any> = this.menuItems) {
    for (let i = 0; i < menuItems.length; i++) {
      menuItems[i].active = false;
      if (isForce) { menuItems[i].opened = false; }

      if (menuItems[i].subItems) {
        this.closeAllSideMenuSubItems(isForce, menuItems[i].subItems);
      }
    }
  }

  updateClock() {
    this.estTime = this.getCurrentTime();
    setInterval(() => {
      this.estTime = this.getCurrentTime();
    }, 1000);
  }

  getCurrentTime() {
    return moment().utc().utcOffset(this.sharedService.offsetEST).format('h:mm:ss');
  }

  changeNetwork($event: any, network: any) {
    const path: any =
      $event.path || ($event.composedPath && $event.composedPath()) || this.sharedService.composedPath($event.target);
    this.sharedService.sendMessage('NETWORKS_WILL_BE_CHANGED', network);
    for (const item in path) {
      if (_.indexOf(path[item].classList, 'networksListCopyButton') > -1) {
        return;
      }
    }

    if (network && network.networkId) {
      const parentURL: string = this.route.snapshot.data.parentURI
        ? this.route.snapshot.data.parentURI
        : this.route.snapshot.firstChild.data.parentURI;

      if (parentURL) {
        this.sharedService.changeNetworkTo = network;
        this.sharedService.sendMessage('NETWORKS_IS_GOING_TO_BE_CHANGED', network);
      }

      this.userService.changeNetwork(network);

      if (this.networkListToggleStatus) {
        this.toggleNetworkList();
      }
    }
  }

  toggleNetworkList(value: boolean = undefined) {
    this.networkListToggleStatus = value === undefined ? !this.networkListToggleStatus : value;
    this.networkFilterInputValue = '';
    this.networkFilterInputValueOld = '';
    this.filteredNetworks = _.cloneDeep(this.sharedService.activeNetworks);
    this.networkListToggleStatus
      ? this.sharedService.sendMessage('NETWORKS_LIST_SHOW')
      : this.sharedService.sendMessage('NETWORKS_LIST_HIDE');
    if (this.networkListToggleStatus) {
      this.hoveredNetwork = _.find(this.sharedService.activeNetworks, { name: this.userService.networkData.name });
      setTimeout(() => {
        const networksList: HTMLElement = <HTMLElement>document.getElementById('networksList');
        const networkFilterInputValue: HTMLElement = <HTMLElement>document.getElementById('networkFilterInputValue');
        const networkListItems: Array<any> = Array.from(document.getElementsByClassName('networksListItem'));
        let currNetworkItem: HTMLElement;
        if (networksList) {
          if (networksList) {
            currNetworkItem = _.find(networkListItems, (item: any) => {
              return item.attributes['data-key'].value === this.hoveredNetwork.name;
            });
            if (currNetworkItem) {
              networksList.scrollTop = currNetworkItem.offsetTop - currNetworkItem.offsetHeight - 8;
            }
          }
          networkFilterInputValue.focus();
        }
      }, 100);
    }
  }

  clearNetworkFilter() {
    this.networkFilterInputValue = '';
    this.networkFilterInputValueOld = '';
    this.onNetworkFilterChange(null);
  }

  onNetworkFilterChange($event: any) {
    let nameFilter: boolean;
    let idFilter: boolean;
    const networksList: HTMLElement = <HTMLElement>document.getElementById('networksList');
    this.filteredNetworks = [];
    for (let i = 0; i < this.sharedService.activeNetworks.length; i++) {
      nameFilter =
        this.sharedService.activeNetworks[i].name.toLowerCase().indexOf(this.networkFilterInputValue.toLowerCase()) >
        -1;
      idFilter =
        this.sharedService.activeNetworks[i].networkId
          .toString()
          .toLowerCase()
          .indexOf(this.networkFilterInputValue.toLowerCase()) > -1;
      if (nameFilter || idFilter) {
        this.filteredNetworks.push(this.sharedService.activeNetworks[i]);
      }
    }
    if (networksList && this.networkFilterInputValueOld === '') {
      networksList.scrollTop = 0;
    }
    this.networkFilterInputValueOld = this.networkFilterInputValue;
    if (_.find(this.filteredNetworks, this.hoveredNetwork) === undefined && this.filteredNetworks.length > 0) {
      this.hoveredNetwork = this.filteredNetworks[0];
    }
  }

  selectNetwork($event: any) {
    if (this.filteredNetworks.length > 0) {
      this.changeNetwork($event, this.hoveredNetwork);
    }
  }

  hoverNetworksList(direction: string) {
    const networksList: HTMLElement = <HTMLElement>document.getElementById('networksList');
    const networkListItems: Array<any> = Array.from(document.getElementsByClassName('networksListItem'));
    let currIndex: number = this.filteredNetworks.indexOf(this.hoveredNetwork);
    let scrollAddition: number = networkListItems[0].offsetHeight;
    if (direction === 'up') {
      currIndex--;
      scrollAddition *= -1;
    } else if (direction === 'down') {
      currIndex++;
    }
    if (currIndex < 0) {
      currIndex = 0;
    } else if (currIndex + 1 > this.filteredNetworks.length) {
      currIndex = this.filteredNetworks.length - 1;
    }
    if (networksList) {
      networksList.scrollTo(0, networksList.scrollTop + scrollAddition);
    }
    this.hoveredNetwork = this.filteredNetworks[currIndex];
  }

  signout(that: SideMenuComponent) {
    that.userService.logout();
  }

  openActiveMenuItem() {
    this.menuItems.forEach((item: any) => {
      if (item.active) {
        this.toggleSideMenuSubItems(item);
      }
    });
  }

  onMenuItemClick(item: any = null, subItem: any = null) {
    if (this.sharedService.sideMenuState === 'small') {
      this.closeAllSideMenuSubItems();
    }
    if (this.sharedService.layout === 'small') {
      this.closeSideMenuOnSmallLayouts();
    }
    if (item !== null && subItem !== null) {
      switch (item.id) {
        case 'recent':
          this.ga.sendEvent(this.ga.MENU, 'Open Report', this.ga.RECENT);
          break;
        case 'myReports':
          if (subItem.label === 'CREATE') {
            this.ga.sendEvent(this.ga.MENU, 'Create', this.ga.MY_REPORTS);
          } else {
            this.ga.sendEvent(this.ga.MENU, 'Open Report', this.ga.MY_REPORTS);
          }
          break;
        case 'supply':
          if (subItem.label === 'MANAGE_SUPPLY') {
            this.ga.sendEvent(this.ga.MENU, 'Manage Supply', this.ga.SUPPLY);
          } else if (subItem.label === 'ALL_SUPPLY_REPORT') {
            this.ga.sendEvent(this.ga.MENU, 'All Supply', this.ga.SUPPLY);
          }
          break;
        case 'demand':
          if (subItem.label === 'MANAGE_DEMAND') {
            this.ga.sendEvent(this.ga.MENU, 'Manage Demand', this.ga.DEMAND);
          } else if (subItem.label === 'ALL_DEMAND_REPORT') {
            this.ga.sendEvent(this.ga.MENU, 'All Demand', this.ga.DEMAND);
          }
          break;
      }
    }
  }

  closeSideMenuOnSmallLayouts() {
    if (this.sharedService.layout !== 'large') {
      this.sharedService.toggleSideMenu('closed');
      this.toggleMyAccountMenu(false);
    }
  }

  toggleMyAccountMenu(value: boolean = null) {
    this.sharedService.myAccountMenuToggleStatus =
      value !== null ? value : !this.sharedService.myAccountMenuToggleStatus;
    if (this.sharedService.sideMenuState === 'small' && this.sharedService.myAccountMenuToggleStatus) {
      this.closeAllSideMenuSubItems();
    }
    if (this.sharedService.myAccountMenuToggleStatus) {
      this.updateFixedElementsLocations();
    }
  }

  updateFixedElementsLocations() {
    if (this.sharedService.myAccountMenuToggleStatus) {
      this.myAccountMarginTop = document.getElementById('sideMenu')
        ? document.getElementById('sideMenu').scrollTop * -1
        : 0;
    }
    if (this.networkListToggleStatus) {
      this.networkListMarginTop = document.getElementById('sideMenu')
        ? document.getElementById('sideMenu').scrollTop * -1
        : 0;
    }
  }

  getBackgroundAnimationState() {
    return this.sharedService.sideMenuState === 'big' && this.sharedService.layout !== 'large' ? 'show' : 'hide';
  }

  @HostListener('window:resize', ['$event'])
  onResize($event: any = null) {
    if ($event === 'orientationchange' || $event === 'resize') {
      this.toggleMyAccountMenu(false);
      this.toggleNetworkList(false);
    }
  }

  // On orientation change event-handling
  @HostListener('window:orientationchange', ['$event'])
  onOrientationChange($event: any) {
    this.onResize('orientationchange');
  }

  @HostListener('document:click', ['$event'])
  onDocumentClick($event: any) {
    let found = false;
    let index = 0;
    let clickedOnNetworkButton = false;
    let clickedOnNetworkList = false;
    const path: any =
      $event.path || ($event.composedPath && $event.composedPath()) || this.sharedService.composedPath($event.target);
    /*
            Close my account menu
        */
    while (!found && index < path.length) {
      found = path[index].id === 'userMenuButton' || path[index].id === 'infoBlockLeft';
      if (!found) {
        index++;
      }
    }
    if (!found) {
      this.toggleMyAccountMenu(false);
    }
    /*
      Close sub-menu items on the small side-menu layout
    */
    found = false;
    index = 0;
    if (this.sharedService.sideMenuState === 'small') {
      while (!found && index < path.length) {
        found = path[index].id === 'sideMenu';
        if (!found) {
          index++;
        }
      }
      if (!found) {
        this.closeAllSideMenuSubItems(true);
      }
    } else {
      this.setCurrentMenuItemState();
    }
    /*
            Close change network dropdown
        */
    index = 0;
    while (index < path.length && !clickedOnNetworkButton && !clickedOnNetworkList) {
      clickedOnNetworkButton = path[index].id === 'networkToggleButton';
      clickedOnNetworkList = path[index].id === 'changeNetworkPopUp';
      if (!clickedOnNetworkButton && !clickedOnNetworkList) {
        index++;
      }
    }
    if (!clickedOnNetworkButton && !clickedOnNetworkList && this.networkListToggleStatus) {
      this.toggleNetworkList(false);
    }
  }
}
